import classnames from 'classnames/bind'
import { useTranslate } from 'react-polyglot'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalGridPreset } from '~/@types/grid-preset'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'

import RichText, { RichTextBlocks } from '~/components/Abstracts/RichText'
import Image, { ImageProps } from '~/components/UI/Image'
import InlineCta, { InlineCtaProps } from '~/components/UI/InlineCta'
import Link, { LinkProps } from '~/components/UI/Link'
import SquaredCta, { SquaredCtaProps } from '~/components/UI/SquaredCta'

import { useStyle } from '~/providers/StyleProvider'

import { FAQ } from '~/data/dictionary'

import css from './styles.module.scss'

const IMAGE_SIZES = {
  ITEM: [{ ratio: 1 / 24 }],
  FOOTER: [{ breakpoint: 'md', ratio: 11 / 24 }, { ratio: 24 / 24 }],
}

const cx = classnames.bind(css)

export interface FaqGridItemProps {
  className?: string
  image?: ImageProps
  name?: RichTextBlocks
  link?: InlineCtaProps
}

export interface FaqGridFooterProps {
  className?: string
  image?: ImageProps
  name?: RichTextBlocks
  subtitle?: RichTextBlocks
  link?: SquaredCtaProps
}

export interface FaqGridProps {
  className?: string
  items?: FaqGridItemProps[]
  footer?: FaqGridFooterProps
}

function FaqItem({ className, image, name, link }: FaqGridItemProps) {
  const t = useTranslate()
  const nameStyle = useStyle({
    textPreset: GlobalTextPreset.Title12Haffer,
    color: GlobalThemeColors.Black,
    textStyling: GlobalTextStyling.UpperCase,
  })

  return (
    <Link className={cx(css.FaqItem, className)} {...(link as LinkProps)}>
      <div className={cx(css.content)}>
        {image && (
          <div className={css.imageWrapper}>
            <Image
              className={cx(css.image)}
              sizesFromBreakpoints={IMAGE_SIZES.ITEM}
              layout="responsive"
              {...image}
            />
          </div>
        )}

        <RichText className={cx(css.name, nameStyle)} render={name} />

        {link && (
          <InlineCta
            className={cx(css.link)}
            textPreset={GlobalTextPreset.Cta11Haffer}
            isDiv>
            {t(FAQ.VIEW_ALL)}
          </InlineCta>
        )}
      </div>
    </Link>
  )
}

function FaqFooter({
  className,
  image,
  name,
  subtitle,
  link,
}: FaqGridFooterProps) {
  const nameStyle = useStyle({
    textPreset: GlobalTextPreset.Title12Haffer,
    color: GlobalThemeColors.Black,
    textStyling: GlobalTextStyling.UpperCase,
  })
  const subtitleStyle = useStyle({
    textPreset: GlobalTextPreset.Text12Haffer,
    color: GlobalThemeColors.DoveGray,
  })

  return (
    <Link className={cx(css.FaqFooter, className)} {...(link as LinkProps)}>
      {image && (
        <Image
          className={cx(css.image)}
          sizesFromBreakpoints={IMAGE_SIZES.FOOTER}
          layout="fill"
          objectFit="cover"
          asPlaceholder
          {...image}
        />
      )}

      <div className={cx(css.content)}>
        <RichText className={cx(css.name, nameStyle)} render={name} />

        <RichText
          className={cx(css.subtitle, subtitleStyle)}
          render={subtitle}
        />

        {link && (
          <SquaredCta className={cx(css.link)} isDiv>
            {link?.children}
          </SquaredCta>
        )}
      </div>
    </Link>
  )
}

function FaqGrid({ className, items, footer }: FaqGridProps) {
  const gridStyle = useStyle({ grid: GlobalGridPreset.BASE_GRID })

  return (
    <div className={cx(css.FaqGrid, className, gridStyle)}>
      {items?.length > 0 && (
        <div className={cx(css.grid)}>
          {items?.map((item, index) => (
            <FaqItem
              key={`${item.name}_${index}`}
              className={cx(css.element, css.item)}
              {...item}
            />
          ))}
          {footer && (
            <FaqFooter className={cx(css.element, css.footer)} {...footer} />
          )}
        </div>
      )}
    </div>
  )
}

FaqGrid.defaultProps = {}

export default FaqGrid
